/**
 * <ostro-access-gate> — Ostrovsky pre-release access code form.
 *
 * Markup: snippets/ostrovsky-access-gate.liquid. Lock rule:
 * snippets/ostrovsky-access-locked.liquid. Runbook: OSTROVSKY-PRERELEASE.md.
 *
 * The code is checked by the server, not here. Submitting writes it to the
 * cart as the `_ostro_access` attribute and, in the same request, asks
 * Shopify to render sections/ostrovsky-access-status against the updated
 * cart. That section says granted or denied; on granted the page reloads
 * and Liquid renders the unlocked page (buy buttons, no gate).
 *
 * Three ways in:
 *   - typing the code
 *   - a link with ?access=CODE (for the Klaviyo email) — submitted on load,
 *     then stripped from the address bar so it isn't shared onward
 *   - a code saved from an earlier visit, re-applied once per session. The
 *     cart (and the attribute with it) is replaced after every checkout and
 *     expires after ~14 days idle; this keeps returning buyers unlocked.
 */
(function () {
  'use strict';

  if (!window.customElements || customElements.get('ostro-access-gate')) return;

  var ATTRIBUTE = '_ostro_access';
  var STATUS_SECTION = 'ostrovsky-access-status';
  var URL_PARAM = 'access';
  var STORE_KEY = 'obsidian:ostro-access';
  var RESTORED_KEY = 'obsidian:ostro-access-restored';

  // Storage can throw (Safari private mode, blocked cookies). Never let that
  // stop the form from working — it only costs the remember-me.
  function store(kind) {
    try {
      var s = window[kind];
      var probe = '__ostro__';
      s.setItem(probe, probe);
      s.removeItem(probe);
      return s;
    } catch (e) {
      return null;
    }
  }

  var local = store('localStorage');
  var session = store('sessionStorage');

  // Custom dataLayer hook; no pixel initialization (ARCHITECTURE.md §9).
  // The code itself is never pushed.
  function track(name, payload) {
    window.dataLayer = window.dataLayer || [];
    var data = { event: name, collab_page: 'ostrovsky' };
    for (var key in payload) {
      if (Object.prototype.hasOwnProperty.call(payload, key)) data[key] = payload[key];
    }
    window.dataLayer.push(data);
  }

  function verify(code) {
    var url = (window.routes && window.routes.cart_update_url) || '/cart/update.js';
    var attributes = {};
    attributes[ATTRIBUTE] = code;

    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ attributes: attributes, sections: STATUS_SECTION })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart/update ' + response.status);
        return response.json();
      })
      .then(function (cart) {
        var html = cart && cart.sections && cart.sections[STATUS_SECTION];
        if (!html) throw new Error('missing ' + STATUS_SECTION);
        return html.indexOf('data-ostro-access="granted"') !== -1;
      });
  }

  // Read ?access= and remove it from the address bar straight away.
  function takeCodeFromUrl() {
    if (!window.URL || !window.history || !history.replaceState) return '';
    var url = new URL(window.location.href);
    var code = (url.searchParams.get(URL_PARAM) || '').trim();
    if (!url.searchParams.has(URL_PARAM)) return '';
    url.searchParams.delete(URL_PARAM);
    history.replaceState(history.state, '', url.pathname + url.search + url.hash);
    return code;
  }

  class OstroAccessGate extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      this.form = this.querySelector('form');
      this.input = this.querySelector('.ostro-gate__input');
      this.button = this.querySelector('.ostro-gate__submit');
      this.msg = this.querySelector('.ostro-gate__msg');
      if (!this.form || !this.input) return;

      this.form.addEventListener('submit', this.onSubmit.bind(this));
      this.input.addEventListener('input', this.clearError.bind(this));

      track('collab_access_gate_view', { access_gate: this.classList.contains('ostro-gate--screen') ? 'page' : 'product' });

      var fromUrl = takeCodeFromUrl();
      var saved = local ? local.getItem(STORE_KEY) : '';

      if (fromUrl) {
        this.input.value = fromUrl;
        this.check(fromUrl, 'link');
      } else if (saved && session && !session.getItem(RESTORED_KEY)) {
        // Once per session, so a code that stopped working can't loop reloads.
        session.setItem(RESTORED_KEY, '1');
        this.check(saved, 'restore');
      }
    }

    onSubmit(event) {
      event.preventDefault();
      this.check(this.input.value, 'form');
    }

    check(raw, source) {
      if (this.busy) return;
      var code = (raw || '').trim();

      if (!code) {
        this.showError(this.dataset.msgEmpty);
        return;
      }

      this.setBusy(true);
      this.say(source === 'restore' ? this.dataset.msgRestoring : this.dataset.msgChecking);

      var self = this;
      verify(code)
        .then(function (granted) {
          if (granted) {
            if (local) local.setItem(STORE_KEY, code);
            track('collab_access_granted', { access_source: source });
            self.say(self.dataset.msgGranted, 'ok');
            // Liquid renders the unlocked page; the browser keeps the scroll
            // position, which is where the buy buttons now are.
            window.location.reload();
            return;
          }

          self.setBusy(false);
          track('collab_access_denied', { access_source: source });

          if (source === 'restore') {
            // The saved code was rotated out. Drop it quietly.
            if (local) local.removeItem(STORE_KEY);
            self.say('');
            return;
          }
          self.showError(self.dataset.msgWrong);
        })
        .catch(function () {
          self.setBusy(false);
          if (source === 'restore') {
            self.say('');
            return;
          }
          self.showError(self.dataset.msgError);
        });
    }

    setBusy(busy) {
      this.busy = busy;
      this.classList.toggle('is-busy', busy);
      this.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (this.button) this.button.disabled = busy;
    }

    say(text, tone) {
      if (!this.msg) return;
      this.msg.textContent = text || '';
      if (tone) this.msg.setAttribute('data-tone', tone);
      else this.msg.removeAttribute('data-tone');
    }

    showError(text) {
      this.say(text, 'error');
      this.input.setAttribute('aria-invalid', 'true');
      this.classList.remove('is-invalid');
      // Reflow so the shake replays on every wrong attempt, not just the first.
      void this.offsetWidth;
      this.classList.add('is-invalid');
      this.input.focus();
      this.input.select();
    }

    clearError() {
      if (!this.classList.contains('is-invalid')) return;
      this.classList.remove('is-invalid');
      this.input.removeAttribute('aria-invalid');
      this.say('');
    }
  }

  customElements.define('ostro-access-gate', OstroAccessGate);
})();
